'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import clsx from 'clsx';
import { CAPABILITIES } from '@/content/capabilities';
import { useTranslation } from '@/hooks/useTranslation';
import { fadeUp, staggerContainer, viewportOnce } from '@/lib/motion';
import { responsiveImage, CAPABILITY_WIDTHS, CAPABILITY_SIZES } from '@/lib/responsiveImage';
import s from './CapabilitiesHomeTwo.module.css';

/*
 * CapabilitiesHomeTwo — homepage "Core Capabilities" section.
 *
 * Redesigned 2026-05-26 to the supplied mock: a left header (blue eyebrow +
 * "Core Capabilities." in Geist + "Measurable Outcomes." in Instrument Serif
 * italic via .serif-accent) with the intro paragraph top-right, over a 3-up
 * grid of 6 cards (media graphic + title + copy). Light theme.
 *
 * Content (titles + copy) lives in content/capabilities.js. Per-card images
 * are supplied separately; until a file exists the media box stays a pale
 * placeholder (the onError hides the broken <img>).
 */

export function CapabilitiesHomeTwo() {
  const { t } = useTranslation();

  return (
    <section id="platform" className={s.section}>
      <div className={s.container}>
        <header className={s.header}>
          <span className="lb-eyebrow">{t('PLATFORM CAPABILITIES')}</span>
          <h2 className="type-section-title-xl">
            {t('Turn Customer Signals Into')}{' '}
            <span className="serif-accent type-section-accent type-section-accent-block">
              {t('Business Impact with AI powered CX')}
            </span>
          </h2>
          <p className={clsx('type-subtitle', s.headPara)}>
            {t('From brand monitoring to crisis response — everything your')}{' '}
            <Link href="/solutions/for-cx-teams/" className={s.headLink}>{t('CX')}</Link>{' '}
            {t('and')}{' '}
            <Link href="/solutions/for-marketing-teams/" className={s.headLink}>{t('marketing teams')}</Link>{' '}
            {t('need to act faster and win more.')}
          </p>
        </header>

        <motion.div
          className={s.grid}
          variants={staggerContainer(0.08)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {CAPABILITIES.map((cap) => {
            const img = responsiveImage(cap.image, {
              widths: CAPABILITY_WIDTHS,
              sizes: CAPABILITY_SIZES,
            });
            return (
            <motion.article key={cap.id} variants={fadeUp} className={s.card}>
              <div className={s.cardMedia}>
                <img
                  src={img.src}
                  srcSet={img.srcSet}
                  sizes={img.sizes}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
              <h3 className={s.cardTitle}>{t(cap.title)}</h3>
              <p className={s.cardDesc}>{t(cap.desc)}</p>
            </motion.article>
          );})}
        </motion.div>
      </div>
    </section>
  );
}
